// SOP gate — client side of rj-be utils/sopGate.js.
//
// A gated action (beam load, program create/close, grading) answers
// 422 { details: { code: "SOP_REQUIRED", template } } when its mandatory
// checklist is missing. withSopGate() catches that, opens the checklist
// (SopChecklistHost, mounted once in App.js), and resends the request with
// the filled checklist attached as `sop_run`.
//
//   await withSopGate((sop_run) => axios.put(url, { ...body, sop_run }));
//
// Cancelling the checklist rejects with an AbortError, which catchAxiosError
// ignores, so callers keep their existing try/catch unchanged.

let host = null; // set by SopChecklistHost

export const registerSopHost = (fn) => {
    host = fn;
    return () => {
        if (host === fn) host = null;
    };
};

/** Open the checklist modal. Resolves to a sop_run payload, or null if cancelled. */
export const requestSopChecklist = (template, opts = {}) =>
    host ? host(template, opts) : Promise.resolve(null);

const cancelled = () => Object.assign(new Error("Checklist cancelled"), { name: "AbortError" });

export async function withSopGate(send) {
    try {
        return await send(undefined);
    } catch (err) {
        if (err?.details?.code !== "SOP_REQUIRED") throw err;
        let template = err.details.template;
        let message = null;
        // Loop: the server may hand back a newer template version, or reject a
        // failed checklist — reopen with the reason instead of losing the entry.
        for (;;) {
            const run = await requestSopChecklist(template, { message });
            if (!run) throw cancelled();
            try {
                return await send(run);
            } catch (e2) {
                const code = e2?.details?.code;
                if (code === "SOP_REQUIRED") {
                    template = e2.details.template;
                    message = e2.message;
                    continue;
                }
                if (code === "SOP_FAILED" || code === "SOP_INVALID") {
                    message = e2.message;
                    continue;
                }
                throw e2;
            }
        }
    }
}
